/**
 * 步驟 4「確認座號」的座號規則(kanban 1000217)。
 *
 * 移植自國中批次上傳 kanban 1000190 的簡化版:高中稿紙無單面/雙面之分,
 * 不彈面數選擇彈窗。同時停用 AI 座號辨識與「接續前一位」。
 *
 * 座號規則經兩次需求修正,現況是兩條並存:
 *   檔名判得出的張 → 保留檔名的值,一律不覆寫(PM 2026-08-26:「部分檔案未依
 *     格式命名時,不應影響其他符合格式檔案的座號自動帶入功能」)。當時移除的
 *     是「檔名沒全中就整批覆寫成順號」—— 老師混傳幾張沒改檔名的相機原檔,
 *     連檔名帶對的號碼都默默被蓋掉。
 *   檔名判不出的張 → prefillSeatNumbers 在進入步驟 4 時依上傳順序預填
 *     (PM 2026-08-27:整批空白、要手動填第一張才會往後排,「預期是一進入就
 *     自動帶入順號」)。
 * 8/26 拿掉的是「連檔名判出的一起覆寫」,不是「不准填」,兩者不衝突。
 *
 * 抽成純函式模組的理由同 batchAnnotateNav:規則寫錯的症狀是靜默的
 * (座號悄悄錯位、gate 提前放行),只有抽純才能用測試釘住。
 */

export interface SeatAssignImage {
  /** 是否為 PDF 展開頁(expandFilesWithPdf 的 fromPdf 平行陣列,上傳時記到圖片物件上) */
  fromPdf?: boolean;
  /** 上傳當下座號是否由檔名判別成功(記在物件上,與 fromPdf 同一個做法) */
  seatFromFilename?: boolean;
}

/**
 * 全部座號是不是都來自檔名:上傳的檔案都是原生圖片檔(非 PDF 展開頁)且每張
 * 座號都已由檔名判別成功。
 *
 * 用途已變更(kanban 1000217,PM 2026-08-26 需求修正):原本決定「要不要依上傳
 * 順序自動編號覆寫全部座號」,現在只決定「要不要啟用逐張確認的關卡」——
 * 認不出座號的檔案一律**維持空白**,不得覆寫(需求原文:「部分檔案未依格式
 * 命名時,不應影響其他符合格式檔案的座號自動帶入功能」)。
 *
 * 判斷只看「上傳當下記到物件上的來源」,不看 annotations 目前的 seatNumber:
 * 自動編號跑過一輪後每列座號都非空,拿當下值判斷會把「回步驟 3 刪一張再
 * 前進」誤判成檔名例外 —— 座號留著跳號、gate 與徽章全消失(code review
 * must-fix)。來源記在物件上,增刪圖都不會錯位、也不會被後續覆寫污染。
 */
export function allSeatsFromFilename(
  images: readonly SeatAssignImage[]
): boolean {
  if (images.length === 0) return false;
  return images.every((img) => !img.fromPdf && !!img.seatFromFilename);
}


/**
 * 逐張確認 gate(僅在有檔名判別不出的圖時啟用):老師須逐張看過(目前顯示那張算看過)
 * 才能離開步驟 4。檔名判別例外路徑不加 gate、維持現行流程。
 * visitedCount 允許超出 total(刪圖後 Set 裡可能留著已不存在的索引,
 * 呼叫端已 Math.min 收斂,這裡再守一次)。
 */
export function seatGateSatisfied(
  gateActive: boolean,
  visitedCount: number,
  total: number
): boolean {
  return !gateActive || visitedCount >= total;
}

// ── 送出前的座號分組(自 BatchUpload 抽出,kanban 1000217)────────────────
// 「確認頁序」步驟移除後,分組結果直接送進 handleSubmit;同座號多張圖的
// 面序由這裡的排序規則決定(pageNumber 升冪、無標記排最後、同值依原索引)。

export interface StudentGroup {
  seatNumber: string;
  name: string;
  imageIndices: number[]; // 依頁序排列,指向 uploadedImages
}

export interface GroupableAnnotation {
  seatNumber: string;
  name: string;
  pageNumber: number | null;
}

export function buildStudentGroups(
  annotations: readonly GroupableAnnotation[]
): StudentGroup[] {
  const map = new Map<string, StudentGroup>();
  annotations.forEach((ann, index) => {
    const key = ann.seatNumber;
    if (!map.has(key)) {
      map.set(key, {
        seatNumber: ann.seatNumber,
        name: ann.name,
        imageIndices: [],
      });
    }
    const group = map.get(key)!;
    if (!group.name && ann.name) group.name = ann.name;
    group.imageIndices.push(index);
  });
  const result = Array.from(map.values());
  result.forEach((g) => {
    g.imageIndices.sort((a, b) => {
      const pa = annotations[a].pageNumber ?? 99;
      const pb = annotations[b].pageNumber ?? 99;
      return pa !== pb ? pa - pb : a - b;
    });
  });
  result.sort(
    (a, b) => parseInt(a.seatNumber, 10) - parseInt(b.seatNumber, 10)
  );
  return result;
}

// ── 老師改座號後往後順排(kanban 1000217,PM 2026-08-26 追加需求)──────────
// 自動編號移除後,全部沒依格式命名的那一批(整班掃描、相機原檔)座號全空白,
// 老師得一格一格打 40 次。PM 要的是「改一張、後面跟著排」:老師在第 N 張填了
// 座號,第 N+1 張起自動 N+1、N+2……,但只補系統填得起的部分 ——
//   1. 遇到老師已確認過的張(visitedIndices)就停:那些是他親眼看過的值,
//      不能被後面的動作回頭蓋掉。被改的那張自己永遠在 visited 裡(它正在顯示),
//      所以判斷從下一張開始。
//   2. 遇到座號由檔名判別出來的張:保留檔名的值(那是最可靠的來源),
//      並以它為新基準續排 —— 混傳時檔名判得出的那幾張就是老師的錨點。
//   3. 兩條同時成立時「已確認」優先,也就是停止。
// 被改那張前面的一律不動;不擋重號、不擋超過班級人數(沒有名冊可比對)。

export interface SeatCascadeInput {
  /** 目前每張的座號(對應 annotations 的 seatNumber,原字串不正規化) */
  seatNumbers: readonly string[];
  /** 與 seatNumbers 平行:每張的來源標記(只看 seatFromFilename) */
  images: readonly SeatAssignImage[];
  /** 老師已確認(看過)的張;被改那張自己在裡面不影響,判斷從下一張開始 */
  visitedIndices: ReadonlySet<number>;
  /** 老師改動的是第幾張 */
  changedIndex: number;
  /** 改動前那張的座號,用來判斷「真的有變」 */
  previousSeatNumber: string;
}

/**
 * 回傳順排後的完整座號陣列;不觸發或排完沒有任何一張變動時回傳 null
 * (呼叫端可直接跳過 setState)。
 *
 * 觸發條件(與 PM 確認):改動後是純數字非空、且與改動前不同。清空、改成
 * 一樣的值、非數字都不觸發。被改的那張本身**不會**被這個函式改寫 ——
 * 座號欄的即時輸入已經寫進去了,這裡只處理它後面的張。
 */
export function cascadeSeatNumbers({
  seatNumbers,
  images,
  visitedIndices,
  changedIndex,
  previousSeatNumber,
}: SeatCascadeInput): string[] | null {
  if (changedIndex < 0 || changedIndex >= seatNumbers.length) return null;
  const changed = seatNumbers[changedIndex];
  if (!/^\d+$/.test(changed)) return null; // 空白或非數字 → 不觸發
  if (changed === previousSeatNumber) return null; // 沒真的改 → 不觸發

  // 基準值取數值(手填的前導零如 "05" 保留在該格,續排從 6 開始)
  let base = parseInt(changed, 10);
  // 超出安全整數的座號續排會失真,再大就變成指數表示法("1e+21"),
  // 寫回去會讓「下一步」的純數字檢查永遠不過、按鈕無聲變灰。不順排比較安全。
  if (!Number.isSafeInteger(base)) return null;
  const next = seatNumbers.slice();
  let touched = false;

  for (let i = changedIndex + 1; i < next.length; i++) {
    if (visitedIndices.has(i)) break; // 已確認 → 停止(優先於檔名判別)
    if (images[i]?.seatFromFilename) {
      // 檔名判出的座號不覆寫,並以它為新基準續排。
      // 防禦:標記在但值已被清空/改成非數字時,基準沿用遞增,值仍不覆寫。
      const fromFile = parseInt(next[i], 10);
      base = /^\d+$/.test(next[i]) && !Number.isNaN(fromFile)
        ? fromFile
        : base + 1;
      continue;
    }
    base += 1;
    if (!Number.isSafeInteger(base)) break; // 同上:排到失真就停,已排好的保留
    const value = String(base);
    if (next[i] !== value) touched = true;
    next[i] = value;
  }

  return touched ? next : null;
}

// ── 進入步驟 4 時預先填入座號(kanban 1000217,PM 2026-08-27 追加需求)─────────
// PM 回報:上傳 PDF 進入確認座號時整批空白,要先手動填第一張才會往後順排 ——
// 「預期是一進入就自動帶入順號」。
//
// PDF 為什麼一定是空白:resolveInitialAnnotation 對 fromPdf 的頁一律回空,
// 不套用檔名解析 —— expandFilesWithPdf 把頁命名成「原檔名_頁碼.jpg」,硬拆
// 出來的殘渣會佔住 seatNumber / name(見 batchImageUtils 該函式上方的說明)。
// PM 截圖裡的「3-六級_1.jpg」就是 PDF「3-六級.pdf」展開的第 1 頁,不是老師
// 自己命名的圖片檔。(半形 - 本身是 parseFilename 認得的分隔符:同名的圖片檔
// 直接上傳會判出座號 3、姓名「六級」,QA 實測確認過。)
//
// 與 8/26「檔名判出的座號不得被覆寫」不衝突,兩條並存:
//   已經有值的格子  → 一律不覆寫(檔名判出的、老師手打的都算,8/26 的要求)
//   空白的格子      → 依上傳順序填入,避開已被占用的號碼(8/27 的要求)
// 8/26 移除的是「有一張判不出就整批覆寫、把檔名帶進來的號碼一起蓋掉」,
// 不是「不准填」。
//
// 這裡不需要知道「這一張的座號是誰填的」:檔名判出的值一定非空
// (seatFromFilename = !!parsed.seatNumber),老師手打的也非空,所以「非空」
// 就涵蓋了所有該保護的來源,函式只吃座號陣列。判斷 gate 的
// allSeatsFromFilename 仍看上傳當下記在圖片物件上的來源標記,兩者互不影響。

/**
 * 回傳預填後的完整座號陣列;沒有任何一張需要變動時回傳 null(呼叫端可跳過
 * setState)。
 *
 * 規則只有兩句:**空白的格子才填,填的號碼避開所有已經有的號碼**。
 *
 * 「只填空白」把檔名判出的值與老師手打的值一起保護起來 —— 增刪一張檔案就會
 * 讓呼叫端重跑預填,若連已有值一起重排,老師手打完 40 個座號、回上一步刪掉
 * 一張模糊的照片再前進,那 40 個號碼會被無聲換成 1..39,沒有提示也沒有 undo。
 * 因此這裡不看「這個號碼是誰填的」(程式也沒有記),一律不覆寫。
 *
 * 「避開已有號碼」是因為撞號的後果是靜默的:buildStudentGroups 以座號為 key,
 * 兩張同號會被歸成同一位學生的多面稿紙,一篇作文等於消失。整班掃描 PDF 混傳
 * 個別命名圖(6 頁 PDF ＋ 1_王小明.jpg)直接從 1 排就會撞。改動前這些格子是
 * 空白,canGoNext 的純數字檢查會擋住老師;填滿之後那道關卡不再擋,所以號碼
 * 必須自己避開。
 *
 * 與 cascadeSeatNumbers 的差異(那條是老師改某一張之後往後排):它以檔名判出的
 * 值為新基準時允許基準倒退、也不查撞號 —— 那是 8/26 已驗收的行為,不在本次
 * 範圍內。這裡的基準只進不退(取 max),避免倒退後又排回已占用的號碼。
 */
export function prefillSeatNumbers(
  seatNumbers: readonly string[]
): string[] | null {
  const next = seatNumbers.slice();

  // 已占用的號碼:目前非空且為純數字的值,不分來源(檔名判出的、老師手打的)
  const occupied = new Set<number>();
  for (const seat of next) {
    if (/^\d+$/.test(seat)) occupied.add(parseInt(seat, 10));
  }

  let base = 0;
  let touched = false;

  for (let i = 0; i < next.length; i++) {
    if (next[i] !== "") {
      // 已有值一律不覆寫;是純數字就把基準推到它,後面的號碼接著往下走。
      // 非數字(手打到一半、資料異常)不推基準、也不占號碼。
      if (/^\d+$/.test(next[i])) {
        base = Math.max(base, parseInt(next[i], 10));
      }
      continue;
    }
    // 往後找第一個沒被占用的號碼。基準大到失真後再排下去會變成指數表示法
    // ("1e+21"),寫回去會讓「下一步」的純數字檢查永遠不過、按鈕無聲變灰;
    // 排到這裡就停,前面排好的保留。
    let candidate = base + 1;
    while (Number.isSafeInteger(candidate) && occupied.has(candidate)) {
      candidate += 1;
    }
    if (!Number.isSafeInteger(candidate)) break;
    base = candidate;
    occupied.add(candidate);
    next[i] = String(candidate);
    touched = true;
  }

  return touched ? next : null;
}
